import React, { useRef, useState } from "react";
import { X, Crop, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureFrame } from "@/lib/videoFrames";

/**
 * Full-screen video player that lets a worker scrub to the exact moment in
 * a clip and grab THAT frame for the AI to look at. The video plays entirely
 * on-device (a local object URL) and is never uploaded or stored — only the
 * grabbed still frames leave the device, as images.
 */
export function VideoFrameGrabber({ src, remaining, onGrab, onClose, T = (x) => x }) {
  const videoRef = useRef(null);
  const [grabbed, setGrabbed] = useState(0);
  const [flash, setFlash] = useState(false);
  const canGrab = remaining - grabbed > 0;

  const grab = () => {
    if (!canGrab) return;
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    const frame = captureFrame(video);
    if (frame) {
      onGrab(frame);
      setGrabbed((g) => g + 1);
      setFlash(true);
      setTimeout(() => setFlash(false), 220);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/90 flex flex-col items-center justify-center p-4"
      data-testid="video-grabber-panel"
    >
      <div className="w-full max-w-2xl flex items-center justify-between mb-3 text-white">
        <div className="text-sm font-semibold flex items-center gap-1.5">
          <Crop className="w-4 h-4" /> {T("Scrub to the exact spot, then grab the frame")}
        </div>
        <button type="button" onClick={onClose} aria-label={T("Close")} data-testid="video-grabber-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="relative w-full max-w-2xl">
        <video
          ref={videoRef}
          src={src}
          controls
          playsInline
          className="w-full max-h-[65vh] rounded-sm bg-black"
          data-testid="video-grabber-video"
        />
        {flash && <div className="absolute inset-0 bg-white/70 rounded-sm pointer-events-none animate-pulse" />}
      </div>

      <div className="w-full max-w-2xl mt-4 flex items-center justify-between gap-3">
        <span className="text-xs text-white/70" data-testid="video-grabber-count">
          {grabbed > 0
            ? T("{n} frame(s) grabbed").replace("{n}", String(grabbed))
            : T("No frames grabbed yet")}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={grab}
            disabled={!canGrab}
            className="rounded-sm"
            data-testid="video-grabber-grab"
          >
            <Crop className="w-4 h-4 mr-2" />
            {canGrab ? T("Grab this frame") : T("Max frames reached")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            className="rounded-sm"
            data-testid="video-grabber-done"
          >
            <Check className="w-4 h-4 mr-2" /> {T("Done")}
          </Button>
        </div>
      </div>
    </div>
  );
}
